import { images } from '@/asset';
import RegularText from '@/components/RegularText';
import CustomCard from '@/components/customCard';
import Layout from '@/components/layout';
import { openCrisp } from '@/helpers';
import { capitalizeFirstLetter } from '@/helpers/string';
import EstimatedApp from '@/pages/Home/components/PopupContents/EstimatedApp';
import { apiCaller } from '@/redux/query';
import { isSkipApiSelector } from '@/redux/slice/auth.slice';
import bannerSlice from '@/redux/slice/banner.slice';
import { dashboardDataSelector } from '@/redux/slice/dashboard.slice';
import { Card, Divider, Icon, InlineGrid, Link, Modal, SkeletonBodyText, Text } from '@shopify/polaris';
import {
  ButtonIcon,
  ChannelsIcon,
  ChatIcon,
  PlanIcon,
  QuestionCircleIcon,
  SendIcon,
  SmileyHappyIcon,
} from '@shopify/polaris-icons';
import { memo, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Table from '../Widgets/component/Table';
import Analytics from './components/Analytics';
import { HomeStyled } from './styled';

interface ActionItemProps {
  content: React.ReactNode;
  prefix: React.ReactNode;
  isHiddenDivider?: boolean;
  disable?: boolean;
  onAction?: () => void;
}

const HomePage = () => {
  const dispatch = useDispatch();
  const isSkip = useSelector(isSkipApiSelector);
  const dashboard = useSelector(dashboardDataSelector);
  const { data, isLoading } = apiCaller.useGeneralSettingsQuery(undefined, { skip: isSkip });
  const [isShowModalEstimatedApp, setIsShowModalEstimatedApp] = useState(false);

  const ActionItems = ({ content, prefix, isHiddenDivider, disable, onAction }: ActionItemProps) => {
    return (
      <div style={{ cursor: onAction ? 'pointer' : 'auto' }} onClick={onAction} className={disable ? 'disable' : ''}>
        <div className="action-item-container">
          <div className="d-flex">
            <span>{prefix}</span> <div className="ml-8">{content}</div>
          </div>
        </div>
        {!isHiddenDivider && <Divider borderColor="border" />}
      </div>
    );
  };
  const listAction: Array<ActionItemProps> = useMemo(() => {
    const result = [
      {
        content: (
          <Text variant="bodyMd" as="p">
            Omega Chat is <b>{capitalizeFirstLetter(data?.data?.plan || '')}</b>
          </Text>
        ),
        prefix: <Icon source={PlanIcon} tone="subdued" />,
      },
      {
        content: (
          <Text variant="bodyMd" as="p">
            <b>{data?.data?.countGroupButtons}</b> widgets created
          </Text>
        ),
        prefix: <Icon source={ChannelsIcon} tone="subdued" />,
      },
      {
        content: (
          <Text variant="bodyMd" as="p">
            You have a total of <b>{data?.data?.countButtons || 0}</b> buttons
          </Text>
        ),
        prefix: <Icon source={ButtonIcon} tone="subdued" />,
      },
      {
        content: (
          <Text variant="bodyMd" as="p">
            Get <b>help and support</b>
          </Text>
        ),
        prefix: <Icon source={QuestionCircleIcon} tone="subdued" />,
        onAction: () => {
          try {
            $crisp.push(['do', 'chat:open']);
          } catch (error) {
            console.log(error);
          }
        },
      },
    ];
    return result;
  }, [data?.data?.countButtons, data?.data?.countGroupButtons, data?.data?.plan]);

  return (
    <Layout title="Omega Chat">
      <HomeStyled>
        <RegularText>Let's build a premium chat experience for your leads</RegularText>

        <div className="mt-16 dashboard-container">
          <InlineGrid columns={{ xl: 2, lg: 2, md: 2, sm: 1, xs: 1 }}>
            <div className="dashboard-img">
              <img src={images.dashboard} alt="_blank" />
            </div>
            <Card>
              {isLoading ? (
                <SkeletonBodyText lines={6} />
              ) : (
                listAction.map((item, index) => {
                  return <ActionItems {...item} isHiddenDivider={listAction.length === index + 1} key={index} />;
                })
              )}
            </Card>
          </InlineGrid>
        </div>

        {/* <div className="mt-16">
          <WarningBanner
            isVisible={isShowEmbedBanner && embeddedBanner}
            title="Enable the app embed on your theme"
            onDismiss={() => {
              dispatch(bannerSlice.actions.handleVisibleBanner({ key: 'embeddedBanner', value: false }));
            }}
            hiddenContactSupport
          >
            <RegularText>
              To ensure the Omega Chat works with your theme, please make sure to turn on the app in the Theme Editor.
            </RegularText>
            <div className="mt-8">
              <Button
                onClick={() => {
                  window.open(
                    `https://${config.shop}/admin/themes/current/editor?context=apps&activateAppId=${process.env.REACT_APP_THEME_APP_EXTENSION_ID}/omg-facebook-chat`,
                    '_blank',
                  );
                }}
              >
                Turn on
              </Button>
            </div>
          </WarningBanner>
        </div> */}

        <div className="mt-16">
          {data?.data?.countGroupButtons === 0 ? (
            <Card padding={'400'}>
              <Table endTime={dashboard.endDate.getTime()} startTime={dashboard.startDate.getTime()} />
            </Card>
          ) : (
            <Analytics isInHomePage={true} />
          )}
        </div>

        <div className="mt-16">
          <CustomCard title={'Help center'}>
            <div className="homepage-help-center">
              <InlineGrid columns={{ xl: 3, lg: 3, md: 3, sm: 1, xs: 1 }} gap={'400'}>
                <Card>
                  <Link removeUnderline url="mailto:contact@omegatheme.com" target="_blank">
                    <Icon source={SendIcon} tone="info" /> Get email support
                  </Link>
                  <Text as="p">Email us, and we'll get back to you as soon as possible.</Text>
                </Card>
                <Card>
                  <Link removeUnderline onClick={openCrisp}>
                    <Icon source={ChatIcon} tone="info" />
                    Start live chat
                  </Link>
                  <Text as="p">Talk to us directly via live chat to get help with your question.</Text>
                </Card>
                <Card>
                  <Link removeUnderline url="https://synctrack.gitbook.io/omega-messenger-whatsapp-chat" target="_blank">
                    <Icon source={SmileyHappyIcon} tone="info" />
                    Our help docs
                  </Link>
                  <Text as="p">Find a solution to your problem with our documents and tutorials.</Text>
                </Card>
              </InlineGrid>
            </div>
          </CustomCard>
        </div>

        <Modal
          open={isShowModalEstimatedApp}
          onClose={() => {
            setIsShowModalEstimatedApp(false);
            dispatch(bannerSlice.actions.handleVisibleBanner({ key: 'isVisibleModalEstimatedApp', value: false }));
          }}
          title={''}
          titleHidden
        >
          <Modal.Section>
            <EstimatedApp onClose={() => setIsShowModalEstimatedApp(false)} />
          </Modal.Section>
        </Modal>
      </HomeStyled>
    </Layout>
  );
};

export default memo(HomePage);
